import { useCallback } from 'react'

import { CopyContent, extendCopyContentWithComplianceMessage, HTML_TYPE, TEXT_TYPE, writeCopyContentToClipboard } from '../../../common/copy'
import { useEffectiveConfig } from '../../../common/useEffectiveConfig'
import { parseEventData } from '../../../common/streamingUtils'

import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { StreamingEventType } from '../../../apps/professional-app/fa/models'

import { InputParameterHierarchyType } from '../../../components/InputParameterHierarchy/model'

import { MessageStartEventData, StreamingEvent, TextDeltaEventData } from '../../../models/StreamingAIAgent'
import { DocumentSummaryExecutionResult } from '../../../models/ProcessDocument'
import { DocumentRecord } from '../../../models/Document'
import { DocumentType } from '../../../models/DocumentType'
import { AIAgentType } from '../../../models/AIAgent'

import {
  getEditorStateFromHtml,
  getHtmlContentFromState,
  getTextContentFromState,
} from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/utils'

import { useLazyGetEffectiveNodeQuery, useLazyGetInputParametersHierarchyDefaultQuery } from '../../../store/ai/inputParametersHierarchy'
import {
  clearDocumentSummaryResult,
  selectDocument,
  selectDocumentSummaryResult,
  selectSelectedDocumentType,
  setDocument,
  setDocumentIsNotRelatedToClient,
  setDocumentSummaryAiAgentId,
  setDocumentSummaryExecutionLoading,
  setDocumentSummaryResult,
} from '../../../store/processDocument/document'
import { selectDocumentBatchContact } from '../../../store/processDocument/documentBatch'
import { AIAgentAttachment, documentFileToAttachment, useExecuteAiAgentStreamingMutation } from '../../../store/ai/aiAgent'
import { useUpdateDocumentMutation } from '../../../store/ai/documents'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { selectDocumentType } from '../../../store/processDocument/processDocument'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { addToast } from '../../../store/toast/toastSlice'

export const useFetchDocumentTypeTemplate = () => {
  const accountId = useAppSelector(selectAccountId)
  const currentUserId = useAppSelector(selectSafeAuthenticatedUser).id

  const [getInputParameterHierarchyNode] = useLazyGetEffectiveNodeQuery()
  const [getInputParameterHierarchyDefault] = useLazyGetInputParametersHierarchyDefaultQuery()

  const fetchDocumentTypeTemplate = useCallback(
    async (documentType: DocumentType): Promise<string> => {
      const template = await getInputParameterHierarchyNode(
        {
          accountId,
          type: InputParameterHierarchyType.DOCUMENT_SUMMARY_TEMPLATE,
          typeId: 'summary-template',
          context: { effective: true, userId: currentUserId, documentTypeId: documentType.id, contextType: 'document_type' },
        },
        true,
      ).unwrap()

      if (template) {
        return template.inputParameters['template'] ?? ''
      }

      const defaultTemplate = await getInputParameterHierarchyDefault(
        {
          accountId,
          type: InputParameterHierarchyType.DOCUMENT_SUMMARY_TEMPLATE,
          typeId: documentType.systemDocumentTypeId ?? 'default-summary',
        },
        true,
      ).unwrap()

      if (defaultTemplate) {
        return defaultTemplate.inputParameters['template'] ?? ''
      }

      return ''
    },
    [accountId, currentUserId, getInputParameterHierarchyDefault, getInputParameterHierarchyNode],
  )

  return { fetchDocumentTypeTemplate }
}

export const useDocumentSummary = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const documentSummaryResult = useAppSelector(selectDocumentSummaryResult)
  const selectedDocumentType = useAppSelector(selectSelectedDocumentType)
  const documentType = useAppSelector(selectDocumentType)
  const contact = useAppSelector(selectDocumentBatchContact)
  const document = useAppSelector(selectDocument)

  const { tracked } = useTracking()

  const [triggerExecuteAiAgentStreaming] = useExecuteAiAgentStreamingMutation()
  const [triggerUpdateDocument] = useUpdateDocumentMutation()
  const { fetchDocumentTypeTemplate } = useFetchDocumentTypeTemplate()

  const { copyComplianceMessage } = useEffectiveConfig()

  const onStreamingEvent = useCallback(
    async (streamingEvent: StreamingEvent) => {
      const parsedEventData = parseEventData(streamingEvent.data)
      switch (streamingEvent.event) {
        case StreamingEventType.MESSAGE_START: {
          const { newAiAgentId } = parsedEventData as MessageStartEventData
          if (newAiAgentId) {
            dispatch(setDocumentSummaryAiAgentId(newAiAgentId))
          }
          break
        }
        case StreamingEventType.TEXT_DELTA: {
          const { data } = parsedEventData as TextDeltaEventData<DocumentSummaryExecutionResult>
          if (!data) {
            return
          }
          const { summary, documentIsNotRelatedToClient } = data
          if (summary !== undefined) {
            dispatch(setDocumentSummaryResult({ summary }))
          }
          if (documentIsNotRelatedToClient) {
            dispatch(setDocumentIsNotRelatedToClient(true))
          }
          break
        }
      }
    },
    [dispatch],
  )

  const summarizeDocument = useCallback(
    async (documentType: DocumentType, document: DocumentRecord): Promise<void> => {
      if (!contact) {
        return
      }

      const { id: documentId, files } = document
      const { id: contactId, name: contactName } = contact

      const attachments: AIAgentAttachment[] = files.map(documentFileToAttachment)

      dispatch(setDocumentSummaryExecutionLoading(true))
      dispatch(clearDocumentSummaryResult())
      try {
        const template = await fetchDocumentTypeTemplate(documentType)
        await triggerExecuteAiAgentStreaming({
          accountId,
          payload: {
            type: AIAgentType.DOCUMENT_SUMMARY,
            context: {
              documentId,
              contactId,
              contactName,
              template,
            },
            attachments,
          },
          onStreamingEvent,
        })
      } catch (e) {
        console.error(e)
        throw e
      } finally {
        dispatch(setDocumentSummaryExecutionLoading(false))
      }
    },
    [accountId, contact, dispatch, fetchDocumentTypeTemplate, onStreamingEvent, triggerExecuteAiAgentStreaming],
  )

  const _handleRerun = useCallback(async () => {
    if (!selectedDocumentType || !document) {
      return
    }

    await summarizeDocument(selectedDocumentType, document)

    try {
      const updatedDocument = await triggerUpdateDocument({
        accountId,
        documentId: document.id,
        payload: { typeProcessing: { typeId: selectedDocumentType.id } },
      }).unwrap()
      dispatch(setDocument(updatedDocument))
    } catch (e) {
      console.error(e)
    }
  }, [accountId, dispatch, document, selectedDocumentType, summarizeDocument, triggerUpdateDocument])

  const handleRerun = tracked(
    _handleRerun,
    { eventName: TrackEvent.DOCUMENT_SUMMARY_TYPE_CHANGED, eventDetails: { documentId: document?.id } },
    { condition: !!selectedDocumentType && !!documentType && selectedDocumentType.id !== documentType.id },
  )

  const getCopyContent = useCallback(() => {
    const { summary = '' } = documentSummaryResult ?? {}

    const editorState = getEditorStateFromHtml(summary)
    const lexicalState = JSON.stringify(editorState)

    return {
      [TEXT_TYPE]: getTextContentFromState(lexicalState),
      [HTML_TYPE]: getHtmlContentFromState(lexicalState),
    }
  }, [documentSummaryResult])

  const copyContentToClipboard = useCallback(
    async (copyContent: CopyContent) => {
      await writeCopyContentToClipboard(copyContent)
      dispatch(addToast({ variant: 'success', title: 'Copied', messages: ['Copied to Clipboard!'] }))
    },
    [dispatch],
  )

  const handleCopy = useCallback(async () => {
    const copyContent = extendCopyContentWithComplianceMessage(getCopyContent(), copyComplianceMessage)
    await copyContentToClipboard(copyContent)
  }, [copyComplianceMessage, copyContentToClipboard, getCopyContent])

  return { handleCopy, handleRerun }
}
